import sharedSupabase from '../config/supabase';
import { resolveEntry, totalFromEntries } from '../utils/countUnitModel.js';

/**
 * Raw count entries — the working behind a count line's single number.
 *
 * `count_items.actual_quantity` stays exactly what it has always been: how much
 * is on hand, in the item's count unit, and the only number ordering,
 * valuation and reporting read. What it never recorded is how it was arrived
 * at. "10.125 gal" of mayonnaise might be a full Costco tub, two Sysco cases,
 * eight loose bottles and an open tub — four facts flattened into one, which
 * nobody can audit or correct without recounting the lot.
 *
 * `count_item_quantity_entries` (migration 20260730120000) holds those four
 * facts. This service reads and writes them, and keeps `actual_quantity` in
 * step so no existing consumer has to know they exist.
 *
 * Two rules the whole file is built around:
 *
 *   A line with no entries was counted the old way. That is not the same as a
 *   line counted as zero, and this service never converts one into the other:
 *   `saveEntries` with an empty list clears the detail and leaves
 *   `actual_quantity` alone.
 *
 *   An entry whose package size is unconfirmed is stored with a NULL
 *   normalized quantity and excluded from the total. Writing 0 there would
 *   understate the shelf and overstate the order.
 *
 * The table arrives with a migration that may not be deployed everywhere, so
 * every read degrades to an empty result and every write reports failure
 * rather than breaking a count in progress.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const TABLE = 'count_item_quantity_entries';

// PostgREST/Postgres "not deployed yet" signatures — missing table or column.
const isMissingSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return (
    ['PGRST204', 'PGRST205', '42703', '42P01'].includes(code) ||
    /column .* does not exist|relation .* does not exist|schema cache/i.test(message)
  );
};

const warnMissing = (error) => {
  console.warn(
    `[count-entries] ${TABLE} unavailable (migration not deployed?):`,
    error?.message,
  );
};

const SELECT_COLUMNS = [
  'id',
  'store_id',
  'count_session_id',
  'count_item_id',
  'inventory_item_id',
  'purchase_option_id',
  'entry_kind',
  'entered_quantity',
  'entered_unit',
  'inner_pack_quantity',
  'normalized_quantity',
  'normalized_unit',
  'conversion_status',
  'conversion_snapshot',
  'conversion_provenance',
  'conversion_confidence',
  'conversion_warnings',
  'location',
  'sort_order',
].join(', ');

/**
 * Entries for a set of count lines, grouped by count_item_id and in display
 * order.
 *
 * @returns {Promise<Object<string, Array>>} {} when the table is not deployed,
 *   which every caller reads as "no package detail" — the legacy shape.
 */
export async function fetchEntriesForCountItems(countItemIds, client = sharedSupabase) {
  const ids = [...new Set((countItemIds || []).filter((id) => id != null))];
  if (ids.length === 0) return {};

  const { data, error } = await client
    .from(TABLE)
    .select(SELECT_COLUMNS)
    .in('count_item_id', ids)
    .order('sort_order', { ascending: true });

  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissing(error);
      return {};
    }
    throw new Error(error.message);
  }

  const byCountItem = {};
  (data || []).forEach((row) => {
    const key = String(row.count_item_id);
    if (!byCountItem[key]) byCountItem[key] = [];
    byCountItem[key].push(row);
  });
  return byCountItem;
}

/**
 * Shapes one editor row into a database row.
 *
 * `normalized_quantity` is only ever written from a resolution the caller
 * already has — never from a fresh guess here — and an unresolved entry is
 * written with NULL, which the table's CHECK constraint also enforces.
 */
function toRow(entry, context, index) {
  const resolvedOk = entry.conversion_status !== 'unresolved' && entry.normalized_quantity != null;
  return {
    store_id: context.storeId,
    count_session_id: context.countSessionId ?? null,
    count_item_id: context.countItemId,
    inventory_item_id: context.inventoryItemId ?? null,
    purchase_option_id: entry.purchase_option_id ?? null,
    entry_kind: entry.entry_kind || 'canonical_measure',
    entered_quantity: Number(entry.entered_quantity),
    entered_unit: entry.entered_unit ?? null,
    inner_pack_quantity: entry.inner_pack_quantity ?? null,
    normalized_quantity: resolvedOk ? entry.normalized_quantity : null,
    normalized_unit: resolvedOk ? entry.normalized_unit ?? context.countUnit ?? null : null,
    conversion_status: resolvedOk ? 'valid' : 'unresolved',
    conversion_snapshot: entry.conversion_snapshot ?? null,
    conversion_provenance: entry.conversion_provenance ?? null,
    conversion_confidence: entry.conversion_confidence ?? null,
    conversion_warnings: entry.conversion_warnings?.length ? entry.conversion_warnings : null,
    location: entry.location ?? context.location ?? null,
    sort_order: entry.sort_order ?? index,
    created_by: context.userId ?? null,
  };
}

/**
 * Replaces the entries for one count line and returns the new total.
 *
 * Delete-then-insert rather than a diff: the entries are a short list a person
 * just retyped, and matching them up by identity would invent edits nobody
 * made. The delete is scoped to the one count line.
 *
 * The write order matters. Entries land first; `actual_quantity` is updated
 * only after they succeed, so a failure leaves the line showing its previous
 * total rather than a total with no working behind it.
 *
 * @param {object} args
 * @param {number|string} args.countItemId
 * @param {number|string} args.storeId
 * @param {Array}  args.entries    editor rows (already resolved by the caller)
 * @param {Array}  [args.rungs]    ladder, used to recompute the total locally
 * @param {boolean} [args.updateActualQuantity=true]
 *   Whether the recomputed total is also written to the count line. The
 *   package sheet says yes — its total IS the count by design. The typed-text
 *   mirror says no: the count the user typed is already persisted through the
 *   ordinary count path, and a background mirror recomputing it through its
 *   own ladder read must never move a number the counter has settled on.
 * @returns {Promise<{ ok:boolean, total:number|null, hasUnresolved:boolean,
 *                     entries:Array, reason:string|null }>}
 */
export async function saveEntriesForCountItem(
  {
    countItemId,
    storeId,
    countSessionId = null,
    inventoryItemId = null,
    countUnit = null,
    location = null,
    userId = null,
    entries = [],
    rungs = [],
    updateActualQuantity = true,
  } = {},
  client = sharedSupabase,
) {
  if (!countItemId) throw new Error('countItemId is required');
  if (!storeId) throw new Error('storeId is required');

  const context = { countItemId, storeId, countSessionId, inventoryItemId, countUnit, location, userId };
  const { total, hasUnresolved, resolved, unresolved } = totalFromEntries(entries, rungs);

  // Resolve each entry individually for its stored row rather than matching it
  // back out of the aggregate — two entries can legitimately share a
  // sort_order, and pairing them up by value would attach one entry's
  // conversion to another's quantity.
  const rows = (entries || []).map((entry, index) => {
    const resolution = resolveEntry(entry, rungs);
    return toRow(
      {
        ...entry,
        normalized_quantity: resolution.status === 'valid' ? resolution.normalized : null,
        normalized_unit: countUnit,
        conversion_status: resolution.status === 'valid' ? 'valid' : 'unresolved',
        conversion_warnings: resolution.warnings,
      },
      context,
      index,
    );
  });

  const { error: deleteError } = await client.from(TABLE).delete().eq('count_item_id', countItemId);
  if (deleteError) {
    if (isMissingSchemaError(deleteError)) {
      warnMissing(deleteError);
      return { ok: false, total, hasUnresolved, entries: [], reason: 'not_deployed' };
    }
    throw new Error(deleteError.message);
  }

  if (rows.length > 0) {
    const { error: insertError } = await client.from(TABLE).insert(rows);
    if (insertError) {
      if (isMissingSchemaError(insertError)) {
        warnMissing(insertError);
        return { ok: false, total, hasUnresolved, entries: [], reason: 'not_deployed' };
      }
      throw new Error(insertError.message);
    }
  }

  // An empty entry list means the detail was cleared, not that the shelf is
  // empty — leave the count line's number alone for the counter to set.
  if (rows.length > 0 && updateActualQuantity) {
    const update = { actual_quantity: total };
    const { error: updateError } = await client.from('count_items').update(update).eq('id', countItemId);
    if (updateError) throw new Error(updateError.message);
  }

  return {
    ok: true,
    total,
    hasUnresolved,
    entries: [...resolved, ...unresolved].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    reason: null,
  };
}

/**
 * Removes all package detail for a count line without touching its total.
 * Used when a counter switches a line back to plain single-number entry.
 */
export async function clearEntriesForCountItem(countItemId, client = sharedSupabase) {
  if (!countItemId) throw new Error('countItemId is required');
  const { error } = await client.from(TABLE).delete().eq('count_item_id', countItemId);
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissing(error);
      return false;
    }
    throw new Error(error.message);
  }
  return true;
}
